import type { JSONSchema7 } from 'json-schema';
import { Category } from './category';

export type TableSchema = Omit<JSONSchema7, 'properties'> & {
	title: string; // make required
	properties: {
		[key: string]: PropSchema;
	};
	implements: string[];
	abbrev?: string;
	'agg-tables'?: string[];
	metadata?: boolean;
};

// Bij het dereferencen worden de FkSchema properties gemerged met de TableSchema/EnumSchema properties.
// Dit moeten we weer ongedaan maken.
export const omitTableSchemaKeys: { [k in keyof TableSchema]: undefined } = {
	$schema: undefined,
	title: undefined,
	properties: undefined,
	implements: undefined,
	abbrev: undefined,
	'agg-tables': undefined,
	metadata: undefined,
};

export type PropSchema = JSONSchema7 & (FieldSchema | FkSchema | ArraySchema<FkSchema> | EnumRefSchema);

export type FkSchema = {
	include?: string[];
	exclude?: string[];
	labelSuffix?: string;
	labelOverride?: {
		[key: string]: string;
	};
	categoryOverride?: {
		[key: string]: Category;
	};
	descriptionOverride?: {
		[key: string]: string;
	};
	'descriptionOverride-somtoday'?: {
		[key: string]: string;
	};
	'descriptionOverride-magister'?: {
		[key: string]: string;
	};
	schema: TableSchema;
};

export const omitFkSchemaKeys: { [k in keyof FkSchema]: undefined } = {
	include: undefined,
	exclude: undefined,
	labelSuffix: undefined,
	labelOverride: undefined,
	categoryOverride: undefined,
	descriptionOverride: undefined,
	'descriptionOverride-somtoday': undefined,
	'descriptionOverride-magister': undefined,
	schema: undefined,
};

export type ArraySchema<T> = {
	type: 'array';
	items: T;
};

export type FieldSchema = {
	label?: string;
	description?: string;
	category?: Category;
	example?: string;
	searchKeys?: string[];
	filter?: boolean;
	grouping?: boolean;
	exportable?: boolean;
	datePicker?: boolean;
	'description-somtoday'?: string;
	'description-magister'?: string;
	blocklist?: {
		bestuur?: boolean;
		geenVO?: boolean;
		geenVSO?: boolean;
		magister?: boolean;
		somtoday?: boolean;
	};
	allowlist?: {
		beheer?: boolean;
	};
};

export type EnumRefSchema = {
	schema: EnumSchema;
};

export type EnumSchema = JSONSchema7 & {
	title: string; // make required
};

export const omitEnumSchemaKeys: { [k in keyof EnumSchema]: undefined } = {
	$schema: undefined,
	title: undefined,
	enum: undefined,
};

export function isTableSchema(schema: JSONSchema7): schema is TableSchema {
	return schema.type === 'object' && Boolean(schema.title);
}

export function isEnumSchema(schema: JSONSchema7): schema is EnumSchema {
	return schema.type === 'string' && Boolean(schema.title) && Boolean(schema.enum);
}

export function isFkSchema(prop: PropSchema): prop is FkSchema {
	// return typeof val === 'object' && val !== null && 'schema' in val && typeof (val as any).schema?.properties === 'object';
	return 'schema' in prop && isTableSchema(prop.schema);
}

export function isEnumRefSchema(prop: PropSchema): prop is EnumRefSchema {
	return 'schema' in prop && isEnumSchema(prop.schema);
}

export function isArrayFkSchema(prop: PropSchema): prop is ArraySchema<FkSchema> {
	// return typeof val === 'object' && val !== null && (val as any).type === 'array' && isFkSchema((val as any).items);
	return prop.type === 'array' && isFkSchema(prop.items as PropSchema);
}

export function getFkSchema(prop: PropSchema): FkSchema | undefined {
	if (isFkSchema(prop)) return prop;
	if (isArrayFkSchema(prop)) return prop.items;
	return undefined;
}

// kan ook een enumref zijn
export function isFieldSchema(prop: PropSchema): prop is FieldSchema {
	return !isFkSchema(prop) && !isArrayFkSchema(prop);
}
